import Raven from 'raven-js';
import { useCallback, useEffect, useRef } from 'react';
import { useSendOnCopilotChannel } from '../channel/hooks/useSendOnCopilotChannel';
import { useIsAssistantBusy } from '../magpie/hooks/useIsAssistantBusy';
/**
 * @deprecated Use `useSendBreezeMessage` from `copilot-toolkit/hooks/useSendBreezeMessage`
 * instead. That hook is BZE-native: its payload extends `OpenPanelOptions` directly, giving
 * you `agentId`, `messageContexts`, `turnMetadata`, `correlationId`, and `hidden` without
 * the legacy `MessageMetaInfo` wrapper. `useSendMessageFromAppToCopilot` is translated into
 * a `SEND_BREEZE_MESSAGE` event by `CopilotChannelListener` anyway — call the right thing
 * from the start.
 *
 * Migration:
 * ```ts
 * // Before
 * const send = useSendMessageFromAppToCopilot();
 * send({ messageText: 'hello', messageMetaInfo: { source: 'CHAT' }, hidden: true });
 *
 * // After
 * const send = useSendBreezeMessage();
 * send({ messageText: 'hello', hidden: true });
 * // For isCollabMode tracking (replaces source: 'PLUGIN'):
 * send({ messageText: 'hello', trackingProps: { isCollabMode: true } });
 * ```
 */
export const useSendMessageFromAppToCopilot = () => {
  const sendCopilotMessageOnChannel = useSendOnCopilotChannel('SEND_MESSAGE');
  const {
    isAssistantBusy
  } = useIsAssistantBusy();
  const isAssistantBusyRef = useRef(isAssistantBusy);
  isAssistantBusyRef.current = isAssistantBusy;
  const pendingMessageRef = useRef(null);
  useEffect(() => {
    if (!isAssistantBusy && pendingMessageRef.current) {
      sendCopilotMessageOnChannel(pendingMessageRef.current);
      pendingMessageRef.current = null;
    }
  }, [isAssistantBusy, sendCopilotMessageOnChannel]);
  return useCallback(data => {
    if (!data.messageText) return;
    if (!isAssistantBusyRef.current) {
      sendCopilotMessageOnChannel(data);
    } else {
      if (pendingMessageRef.current) {
        var _pendingMessageRef$cu, _pendingMessageRef$cu2;
        // we'll only queue one message at a time
        Raven.captureMessage('Message not sent to copilot because assistant is busy', {
          level: 'warning',
          extra: {
            droppedMessageLength: (_pendingMessageRef$cu = (_pendingMessageRef$cu2 = pendingMessageRef.current.messageText) === null || _pendingMessageRef$cu2 === void 0 ? void 0 : _pendingMessageRef$cu2.length) !== null && _pendingMessageRef$cu !== void 0 ? _pendingMessageRef$cu : 0
          }
        });
      }
      pendingMessageRef.current = data;
    }
  }, [sendCopilotMessageOnChannel]);
};
export const useSendCollaborationPluginContext = () => {
  const sendCopilotMessageOnChannel = useSendOnCopilotChannel('COLLABORATE_COPILOT_SET_CONTEXT');
  return useCallback(({
    plugin,
    extraIntentProperties
  }) => {
    sendCopilotMessageOnChannel({
      plugin,
      extraIntentProperties: extraIntentProperties || {}
    });
  }, [sendCopilotMessageOnChannel]);
};
export const useSendCollaborationAction = () => {
  const sendCopilotActionOnChannel = useSendOnCopilotChannel('SEND_ACTION');
  return useCallback(({
    messageAction,
    messageMetaInfo,
    customAssistantData,
    customAssistantId
  }) => {
    sendCopilotActionOnChannel({
      messageAction,
      messageMetaInfo,
      customAssistantData,
      customAssistantId
    });
  }, [sendCopilotActionOnChannel]);
};
export const useAskUserForMoreInfo = () => {
  const sendCopilotActionOnChannel = useSendOnCopilotChannel('PROMPT_USER_FOR_MORE_INFO');
  return useCallback(({
    messageAction,
    messageMetaInfo,
    promptData
  }) => {
    sendCopilotActionOnChannel({
      messageAction,
      messageMetaInfo,
      promptData
    });
  }, [sendCopilotActionOnChannel]);
};